import { createTRPCProxyClient, httpBatchLink, TRPCClientError } from '@trpc/client';
import type { ApiRouter } from '@projects/students-types';
import { mapTrpcError } from './errorMapper';

let trpcUrl = '';
let tokenProvider: (() => string | null) | null = null;
let client: ReturnType<typeof createTRPCProxyClient<ApiRouter>> | null = null;

/** Set the tRPC server URL (call once at startup). */
export function setTrpcUrl(url: string): void {
  trpcUrl = url;
}

/** Provide a function that returns the current auth token (call once at startup). */
export function setTokenProvider(fn: () => string | null): void {
  tokenProvider = fn;
}

/** Lazy singleton — creates the tRPC proxy client on first access. */
export function getTrpcClient(): ReturnType<typeof createTRPCProxyClient<ApiRouter>> {
  if (!client) {
    client = createTRPCProxyClient<ApiRouter>({
      links: [
        httpBatchLink({
          url: trpcUrl,
          headers() {
            const token = tokenProvider?.();
            if (token) {
              return { Authorization: token };
            }
            return {};
          },
        }),
      ],
    });
  }
  return client;
}

/**
 * Wrapper that catches TRPCClientError and rethrows mapped app errors
 * so ErrorBoundary.vue handles them as usual.
 */
export async function trpcCall<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof TRPCClientError) {
      throw mapTrpcError(err);
    }
    throw err;
  }
}

/** Reset the singleton (e.g. on logout). */
export function resetTrpcClient(): void {
  client = null;
}
